/* IndexedDB schema and access. The store is the source of truth on each device;
   §6 sync only ever ships an encrypted copy of what is here. */

import { openDB, type DBSchema, type IDBPDatabase, type StoreNames } from 'idb';
import { sortBands } from '../lib/capBands';
import type { LimitOverrides } from '../rules/limits';
import type {
  AccountRecord, CapAssignment, CashBalance, CashConversion, CashFlow, Dividend, DividendHistory,
  EarningsDate,
  AlertEpisode, Financials, FxRates, Holding, ImportBatch, Logo, Perp, PerpMark, Quote, Snapshot,
  CapBandRecord, ShareTransfer, ThemeRecord, Thesis, Trade, Watch,
} from '../types';
import {
  SEED_ACCOUNTS, SEED_CAP_BANDS, SEED_CAPS, SEED_CASH, SEED_FX, SEED_HOLDINGS, SEED_PERPS,
  SEED_QUOTES, SEED_SNAPSHOTS, SEED_THEMES,
} from './seed';

const DB_NAME = 'position-book';
/* 10 adds `logos`: a company's own mark, cached as bytes so it draws offline
 * and under the macOS shell's `img-src 'self' data:`. Nothing else changed.
 *
 * 9 repairs rather than adds.
 *
 * A database created from nothing at version 8 came up without `conversions`,
 * because the branch that made a new database and the branch that upgraded an
 * old one were two separate lists and only one of them had been told. Those
 * databases are still at 8 and still broken, and a version that matches never
 * runs the upgrade again — so the number is moved to bring them back through
 * it. Nothing else changed: on a database that is already whole, every store
 * exists and the pass does nothing. */
const DB_VERSION = 12;

export interface Settings {
  displayCurrency: 'USD' | 'PLN';
  cycleIsHot: boolean;          // switches cashFloor to cashFloorHot
  seeded: boolean;
  backupAcknowledged: boolean;  // the one-time "the export is the real backup" notice
  pauseStartedAt?: string;      // set when drawdownPause first fires
  workerBase?: string;          // override for the Worker origin
  /** Edited in settings (§7.5); merged on top of the defaults in limits.ts. */
  limitOverrides?: LimitOverrides;
  /** Remembered so a push can tell the other device's write apart from its own. */
  lastSyncedAt?: string;
  /** Which version of the rebuild produced the reconstructed points on the
      chart. Behind the current one means this device is still showing a line
      an older, worse version drew. See REBUILD_GENERATION. */
  rebuiltWith?: number;
  /** Which colour theme is in use. See src/styles/themes.ts. */
  theme?: string;
  /** How far the background wash comes up, as a multiplier on whatever the
      theme set: 0 turns it off, 1 is the theme as designed, 2 is twice as far.
      Absent means 1 — a book that has never touched the dial looks exactly as
      it did before there was one. */
  glowStrength?: number;
  /** Show the book without showing what is in it. Money figures and share
      counts print as dots; weights, prices and dates carry on. Kept in settings
      rather than in the book, so turning it on here does not travel to the
      other device on the next sync — which is the point, since the reason to
      hide is usually about the room you are standing in. */
  hideAmounts?: boolean;
  /** Names and weights written around the ring on the Value screen. Absent
      means on: a chart of unlabelled colours is the thing this replaced. */
  donutLabels?: boolean;
  /** Draw the money-paid-in line on the value chart, and the area between it
      and the value. Absent means off: over a short window the paid-in line sits
      far below the value and stretches the scale across the difference, which
      draws the week as a flat lid on a solid block. On, the band between the
      two lines is what the market added on top of what went in — the right
      picture over a year, where both move together. */
  chartPaidIn?: boolean;
  /** Draw each position's own logo instead of a two-letter monogram. Absent
      means on. Off is not only cosmetic: with it off the app never asks the
      logo service anything, so nothing about what is held leaves the device by
      that route at all. */
  showLogos?: boolean;
  /** What the book is called in the header. It is one person's book, and the
      name it was shipped under is not necessarily theirs. */
  appName?: string;
  /** Tell me when something moves more than this much in a day, in percent.
      Zero, or absent, is off. */
  moveAlertPct?: number;
  /** Symbols excluded from that — a name you know is jumpy and do not want to
      hear about every afternoon. */
  mutedMovers?: string[];
  /* What each broker has been told, keyed by broker id and then by field.
   *
   * Deliberately in settings and not in the book. Settings are outside
   * BOOK_STORES, so this is in neither the JSON export nor the sync blob — the
   * export is a plaintext file the user is told to keep somewhere findable, and
   * a broker key does not belong in one. It stays on the device it was typed
   * into, and travels only to the broker it belongs to.
   *
   * A map rather than a field per broker, because the fields differ per broker
   * and the screen draws whatever `lib/brokers.ts` lists. Adding a broker adds
   * no key here. */
  brokerFields?: Record<string, Record<string, string>>;
  /** Which account each connection files into. */
  brokerAccounts?: Record<string, string>;
  /** Whose form is open on the settings screen. */
  broker?: string;
  /* Whether the futures account is read on its own, without the button.
   *
   * On by default once a key is entered, because a balance that is only right
   * when you remember to press something is usually wrong. Off is a real
   * choice: it costs a request every few minutes and some people would rather
   * a book that only changes when they say so. */
  mexcAuto?: boolean;
  /** When that last succeeded, so the screen can say how current it is. */
  mexcSyncedAt?: string;
  /** What went wrong last time, kept so a key that quietly stopped working is
      visible rather than silently doing nothing. */
  mexcError?: string;
  /* Contracts the book still has open that the exchange has stopped reporting,
   * by API symbol.
   *
   * The sync deliberately never closes anything: closing writes a realised
   * result, and that result is the fill you got rather than something to infer
   * from an absence. But it worked this out every five minutes and then threw
   * it away, so a position closed on the exchange sat in the book saying
   * nothing was wrong — while the free balance beside it reconciled perfectly,
   * which made it look like the book was current. Remembering it is what lets
   * the screen holding that contract say so. */
  mexcGone?: string[];
  /* The spot wallet, read with the same key. See `state/mexcSpot.ts`. Kept
     apart from the futures fields, so a key that can read one and not the other
     says which. */
  mexcSpotSyncedAt?: string;
  mexcSpotError?: string;
  /** Stablecoins tied up in open spot orders, in USD. Part of the cash, not extra. */
  mexcSpotLocked?: number;
  /** Open spot orders as last read, for the pairs this book holds coins in. */
  mexcSpotOrders?: Array<{ symbol: string; side: 'buy' | 'sell'; price: number; quantity: number; filled: number; at: string }>;
  /** Coins the book holds on the spot account that the last read no longer found. */
  mexcSpotGone?: string[];
  /** Coins left out as worth less than a dollar, or with no USDT price to tell. */
  mexcSpotDust?: string[];

  /* The three flat keys this replaced, from when there was one broker and its
     name was written into the field names. Read once and migrated by
     `connectionFor`; never written again. */
  ibkrFlexToken?: string;
  ibkrFlexQueryId?: string;
  ibkrAccount?: string;

  /** Tell me a report or an ex-date is coming this many days ahead. Zero is off. */
  remindDaysAhead?: number;
  /** Whether a shut exchange may be priced from the company's MEXC contract
      index. Default on — see `state/offHours.ts` for what that price is and
      what it is deliberately kept out of. */
  offHoursMarks?: boolean;
  /** Checks switched off in the alerts, by kind. Absent means all of them run,
      so a check added in a later version starts by being heard. */
  checksOff?: string[];
  /** The exchange's free balance and posted margin as of the last account read,
      so the next one can tell money that left the exchange from money moved
      into a contract. See `state/exchangeOutflow.ts`. */
  mexcLastFree?: number;
  mexcLastMargin?: number;
  /** How much the free balance has fallen without an explanation the book can
      work out, still waiting to be accounted for. A running figure: two
      transfers before you answer are one question about their sum. */
  mexcUnaccounted?: number;
  mexcUnaccountedAt?: string;
  /** Draw the holdings split as columns rather than as a ring. A preference,
      so it is remembered — see `components/WeightBars.tsx` for why both. */
  splitAsBars?: boolean;
  /** Checks switched off on one position rather than everywhere, as
      `TICKER:kind`. A rule can be a fair point about eleven holdings and not
      about the twelfth, and turning it off for the book to quiet one of them
      trades a card you did not want for eleven you did. See
      `state/silenced.ts`. */
  silencedChecks?: string[];
  /** Thresholds the user wrote themselves, run alongside the spec's. */
  customRules?: import('../rules/engine').CustomRule[];
  /** Symbols to draw against the book — SPY, QQQ, GLD and so on. More than one
      at a time: "did I beat the index" is a different question for each index,
      and the honest answer is usually "beat one, lost to another". */
  benchmarks?: string[];
  /** The single choice this replaced. Read once and migrated; never written. */
  benchmark?: string;

  /** Where the user files. It fills in the rates below and decides which notes
      the report carries. */
  taxCountry?: string;
  /** Rates in force, as percentages. Absent means "whatever the country sets";
      present means the user typed one, which wins. Zero is a rate. */
  taxGainsPct?: number;
  taxDividendPct?: number;
  /** A picture for the corner, stored as a data URL. Downscaled to a small
      square before it is kept: a phone photograph is several megabytes, and the
      book is the only copy of everything else in this database. */
  avatar?: string;
  /** Shared secret gating the blob endpoints, when the Worker has BLOB_TOKEN
      set. Without it a stranger who finds the Worker URL could overwrite the
      synced copy — the ciphertext stays unreadable either way, but the cloud
      copy could be destroyed. Not a password: it authenticates the endpoint,
      it does not protect the data. The passphrase does that. */
  blobToken?: string;
}

const DEFAULT_SETTINGS: Settings = {
  displayCurrency: 'USD',
  cycleIsHot: false,
  seeded: false,
  backupAcknowledged: false,
};

interface BookDB extends DBSchema {
  holdings:   { key: string; value: Holding; indexes: { ticker: string } };
  theses:     { key: string; value: Thesis };
  trades:     { key: string; value: Trade; indexes: { ticker: string; at: string } };
  cashflows:  { key: string; value: CashFlow };
  conversions: { key: string; value: CashConversion };
  transfers:  { key: string; value: ShareTransfer; indexes: { ticker: string } };
  capBands:   { key: string; value: CapBandRecord };
  snapshots:  { key: string; value: Snapshot };
  perps:      { key: string; value: Perp };
  cash:       { key: string; value: CashBalance };
  themes:     { key: string; value: ThemeRecord };
  caps:       { key: string; value: CapAssignment };
  accounts:   { key: string; value: AccountRecord };
  quotes:     { key: string; value: Quote };
  earnings:   { key: string; value: EarningsDate };
  perpMarks:  { key: string; value: PerpMark };
  financials: { key: string; value: Financials };
  dividends:  { key: string; value: Dividend; indexes: { ticker: string; payDate: string } };
  divHistory: { key: string; value: DividendHistory };
  watchlist:  { key: string; value: Watch; indexes: { ticker: string } };
  importBatches: { key: string; value: ImportBatch; indexes: { account: string } };
  alertLog: { key: string; value: AlertEpisode };
  logos:      { key: string; value: Logo };
  kv:         { key: string; value: unknown };
}

/* A browser may evict IndexedDB when the device runs short of space, and this
   book is the only copy of every thesis the user has written. Asking for
   persistent storage takes it out of the evictable pool. Chrome grants it
   silently to an installed app; Safari grants it after a little use. It cannot
   be relied on — which is why the export in settings is still the real backup —
   but not asking would be leaving the door open for nothing. */
export async function requestDurableStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/* Every store this database holds, and how each one is made.
   
   Adding a store means adding a line here and bumping DB_VERSION — and nothing
   else. There is no second place that also has to be told. */
const STORES = {
  holdings:     (d) => { d.createObjectStore('holdings', { keyPath: 'id' }).createIndex('ticker', 'ticker'); },
  theses:       (d) => { d.createObjectStore('theses', { keyPath: 'ticker' }); },
  trades:       (d) => {
    const trades = d.createObjectStore('trades', { keyPath: 'id' });
    trades.createIndex('ticker', 'ticker');
    trades.createIndex('at', 'at');
  },
  cashflows:    (d) => { d.createObjectStore('cashflows', { keyPath: 'id' }); },
  conversions:  (d) => { d.createObjectStore('conversions', { keyPath: 'id' }); },
  transfers:    (d) => { d.createObjectStore('transfers', { keyPath: 'id' }).createIndex('ticker', 'ticker'); },
  capBands:     (d) => { d.createObjectStore('capBands', { keyPath: 'id' }); },
  snapshots:    (d) => { d.createObjectStore('snapshots', { keyPath: 'at' }); },
  perps:        (d) => { d.createObjectStore('perps', { keyPath: 'id' }); },
  cash:         (d) => { d.createObjectStore('cash', { keyPath: 'account' }); },
  themes:       (d) => { d.createObjectStore('themes', { keyPath: 'name' }); },
  caps:         (d) => { d.createObjectStore('caps', { keyPath: 'ticker' }); },
  quotes:       (d) => { d.createObjectStore('quotes', { keyPath: 'symbol' }); },
  earnings:     (d) => { d.createObjectStore('earnings', { keyPath: 'symbol' }); },
  perpMarks:    (d) => { d.createObjectStore('perpMarks', { keyPath: 'symbol' }); },
  financials:   (d) => { d.createObjectStore('financials', { keyPath: 'symbol' }); },
  dividends:    (d) => {
    const dividends = d.createObjectStore('dividends', { keyPath: 'id' });
    dividends.createIndex('ticker', 'ticker');
    dividends.createIndex('payDate', 'payDate');
  },
  divHistory:   (d) => { d.createObjectStore('divHistory', { keyPath: 'symbol' }); },
  accounts:     (d) => { d.createObjectStore('accounts', { keyPath: 'id' }); },
  watchlist:    (d) => { d.createObjectStore('watchlist', { keyPath: 'id' }).createIndex('ticker', 'ticker'); },
  importBatches: (d) => { d.createObjectStore('importBatches', { keyPath: 'id' }).createIndex('account', 'account'); },
  alertLog:     (d) => { d.createObjectStore('alertLog', { keyPath: 'id' }); },
  logos:        (d) => { d.createObjectStore('logos', { keyPath: 'symbol' }); },
  // Out-of-line keys: the caller supplies one. Everything else keys on itself.
  kv:           (d) => { d.createObjectStore('kv'); },
/* Typed against the schema rather than against `string`, so a store declared
     on BookDB and left out of this list is a compile error. That is the whole
     of the guarantee: the list cannot fall behind again. */
} satisfies Record<StoreNames<BookDB>, (d: IDBPDatabase<BookDB>) => void>;

type StoreName = keyof typeof STORES;

let dbp: Promise<IDBPDatabase<BookDB>> | null = null;

export function db(): Promise<IDBPDatabase<BookDB>> {
  dbp ??= openDB<BookDB>(DB_NAME, DB_VERSION, {
    upgrade(d, oldVersion, _newVersion, tx) {
      /* One list, walked once, whether the database is being made from nothing
         or brought up to date.
         *
         * It used to be two: a branch that created every store for a new
         * device, and a branch that added the missing ones to an old device.
         * They drifted, as two lists of the same thing do — `conversions` was
         * added to the second and not the first, so an existing device upgraded
         * cleanly and a fresh install came up at the current version without
         * the store. Every read of it threw, and the app did not start at all:
         * a new phone, a new browser, a reinstalled desktop app.
         *
         * `createObjectStore` guarded by `contains` does both jobs, so there is
         * nothing left to keep in step. */
      for (const name of Object.keys(STORES) as StoreName[]) {
        if (!d.objectStoreNames.contains(name)) STORES[name](d);
      }

      /* v2 added user-defined brokers. A book that predates it gets the five it
         was seeded with, so every stored holding still resolves to a name. A
         fresh database is left empty for §10's seed to fill. */
      if (oldVersion >= 1 && oldVersion < 2) {
        const store = tx.objectStore('accounts');
        for (const a of SEED_ACCOUNTS) store.put(a);
      }

      /* v12 turned the nine size ceilings from constants into records.
       *
       * An existing book gets the nine under their original ids, with any
       * percentage the user had already edited folded in — those lived in
       * `settings.limitOverrides.positionCaps`, which nothing writes again
       * after this. Without the fold every tuned ceiling would silently revert
       * to the shipped default.
       *
       * Two things here are load-bearing:
       *
       * `oldVersion >= 1` keeps a brand new database out of it, because
       * `seedIfEmpty` fills that one. Two lists of the same thing is exactly
       * what stopped every fresh install from starting at version 8.
       *
       * The overrides are read straight off the `kv` store inside this
       * transaction. `getSettings()` awaits `db()` — which is the very
       * `openDB` promise whose upgrade callback is running — so calling it
       * here does not read a wrong number, it hangs the first launch. */
      if (oldVersion >= 1 && oldVersion < 12) {
        const bands = tx.objectStore('capBands');
        void tx.objectStore('kv').get('settings').then((raw) => {
          const edited = (raw as Settings | undefined)?.limitOverrides?.positionCaps ?? {};
          for (const b of SEED_CAP_BANDS) {
            const pct = edited[b.id];
            bands.put(Number.isFinite(pct) ? { ...b, pct: pct as number } : b);
          }
        });
      }
    },
  });
  return dbp;
}

/* ---------- key/value ---------- */

export async function getKv<T>(key: string): Promise<T | undefined> {
  return (await db()).get('kv', key) as Promise<T | undefined>;
}

export async function setKv(key: string, value: unknown): Promise<void> {
  await (await db()).put('kv', value, key);
}

export async function getSettings(): Promise<Settings> {
  const s = await getKv<Partial<Settings>>('settings');
  return { ...DEFAULT_SETTINGS, ...s };
}

export async function saveSettings(patch: Partial<Settings>): Promise<Settings> {
  const next = { ...(await getSettings()), ...patch };
  await setKv('settings', next);
  return next;
}

/* ---------- cached market data ---------- */

export async function getFx(): Promise<FxRates | undefined> {
  return getKv<FxRates>('fx');
}

export async function saveFx(fx: FxRates): Promise<void> {
  await setKv('fx', fx);
}

export async function allQuotes(): Promise<Quote[]> { return (await db()).getAll('quotes'); }

export async function getDividendHistory(symbol: string): Promise<DividendHistory | undefined> {
  return (await db()).get('divHistory', symbol);
}

export async function saveDividendHistory(rows: DividendHistory[]): Promise<void> {
  const tx = (await db()).transaction('divHistory', 'readwrite');
  await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
}

export async function getFinancials(symbol: string): Promise<Financials | undefined> {
  return (await db()).get('financials', symbol);
}

export async function saveFinancials(rows: Financials[]): Promise<void> {
  const tx = (await db()).transaction('financials', 'readwrite');
  await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
}
export async function allEarnings(): Promise<EarningsDate[]> { return (await db()).getAll('earnings'); }
export async function allPerpMarks(): Promise<PerpMark[]> { return (await db()).getAll('perpMarks'); }

export async function allLogos(): Promise<Logo[]> { return (await db()).getAll('logos'); }

export async function removeLogo(symbol: string): Promise<void> {
  await (await db()).delete('logos', symbol);
}

export async function saveLogos(rows: Logo[]): Promise<void> {
  if (rows.length === 0) return;
  const tx = (await db()).transaction('logos', 'readwrite');
  await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
}

export async function saveQuotes(quotes: Quote[]): Promise<void> {
  const tx = (await db()).transaction('quotes', 'readwrite');
  await Promise.all([...quotes.map((q) => tx.store.put(q)), tx.done]);
}

/** Throws away marks for symbols the book no longer asks about. They can never
    be refreshed again, so they only sit there getting older. */
export async function dropQuotes(symbols: string[]): Promise<void> {
  if (symbols.length === 0) return;
  const tx = (await db()).transaction('quotes', 'readwrite');
  await Promise.all([...symbols.map((s) => tx.store.delete(s)), tx.done]);
}

export async function saveEarnings(rows: EarningsDate[]): Promise<void> {
  const tx = (await db()).transaction('earnings', 'readwrite');
  await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
}

export async function savePerpMarks(rows: PerpMark[]): Promise<void> {
  const tx = (await db()).transaction('perpMarks', 'readwrite');
  await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
}

/* ---------- book contents ---------- */

export interface BookData {
  accounts: AccountRecord[];
  dividends: Dividend[];
  holdings: Holding[];
  theses: Thesis[];
  trades: Trade[];
  cashflows: CashFlow[];
  snapshots: Snapshot[];
  perps: Perp[];
  cash: CashBalance[];
  themes: ThemeRecord[];
  caps: CapAssignment[];
  /** Followed, not owned. Never reaches a total or the rule engine. */
  watchlist: Watch[];
  /** Reversible runs of a broker's trade file. */
  importBatches: ImportBatch[];
  /** Breaches, as they came and went. */
  alertLog: AlertEpisode[];
  /** Cash changed from one currency into another. Never a cash flow. */
  conversions: CashConversion[];
  /** The size ceilings, which are the user's naming system rather than a fixed
      set. In the book rather than in settings because every `CapAssignment`
      points at one by id, and settings are outside `BOOK_STORES` — split them
      and a restored or synced book arrives with assignments naming ceilings the
      receiving device has never had. */
  capBands: CapBandRecord[];
  /** Shares moved between accounts. Never a trade, never a cash flow — but
      unlike a conversion these are read back by the cost and tax arithmetic,
      because after one the purchase that explains a lot sits under a different
      account than the lot does. */
  transfers: ShareTransfer[];
}

export async function loadBook(): Promise<BookData> {
  const d = await db();
  const [accounts, dividends, holdings, theses, trades, cashflows, snapshots, perps, cash, themes, caps, watchlist, importBatches, alertLog, conversions, transfers, capBands] =
    await Promise.all([
      d.getAll('accounts'), d.getAll('dividends'),
      d.getAll('holdings'), d.getAll('theses'), d.getAll('trades'),
      d.getAll('cashflows'), d.getAll('snapshots'), d.getAll('perps'),
      d.getAll('cash'), d.getAll('themes'), d.getAll('caps'), d.getAll('watchlist'),
      d.getAll('importBatches'), d.getAll('alertLog'), d.getAll('conversions'),
      d.getAll('transfers'), d.getAll('capBands'),
    ]);
  return {
    accounts: accounts.sort((a, b) => a.order - b.order),
    dividends: dividends.sort((a, b) => a.payDate.localeCompare(b.payDate)),
    holdings, theses, trades, cashflows, snapshots, perps, cash, themes, caps,
    conversions: conversions.sort((a, b) => b.at.localeCompare(a.at)),
    transfers: transfers.sort((a, b) => b.at.localeCompare(a.at)),
    // Largest ceiling first — see `lib/capBands.ts` for why that order.
    capBands: sortBands(capBands),
    watchlist: watchlist.sort((a, b) => a.ticker.localeCompare(b.ticker)),
    importBatches: importBatches.sort((a, b) => b.at.localeCompare(a.at)),
    alertLog: alertLog.sort((a, b) => b.firstSeen.localeCompare(a.firstSeen)),
  };
}

const BOOK_STORES = [
  'accounts', 'dividends', 'holdings', 'theses', 'trades', 'cashflows', 'snapshots', 'perps',
  'cash', 'themes', 'caps', 'watchlist', 'importBatches', 'alertLog', 'conversions',
  /* A store left off this list is never cleared, so its rows survive a restored
     book and point at lots that no longer exist. */
  'transfers', 'capBands',
] as const;

/** Replaces the entire book. Used by seeding, by JSON import, and by a pull from
    the sync blob — all three want the same all-or-nothing swap. */
export async function replaceBook(data: BookData): Promise<void> {
  const d = await db();
  const tx = d.transaction(BOOK_STORES, 'readwrite');
  await Promise.all(BOOK_STORES.map((name) => tx.objectStore(name).clear()));
  await Promise.all([
    // An import from before brokers were editable carries none; fall back to the
    // five originals so its holdings still resolve to a name.
    ...(data.accounts?.length ? data.accounts : SEED_ACCOUNTS)
      .map((v) => tx.objectStore('accounts').put(v)),
    ...(data.dividends ?? []).map((v) => tx.objectStore('dividends').put(v)),
    ...(data.conversions ?? []).map((v) => tx.objectStore('conversions').put(v)),
    // Absent from every export written before transfers existed.
    ...(data.transfers ?? []).map((v) => tx.objectStore('transfers').put(v)),
    /* Key presence, not length. A length test cannot tell "this export predates
       ceilings" from "I deleted every ceiling", and deleting them is a thing
       this book now lets you do. The `accounts` line above has the same latent
       problem and is left alone rather than changed in passing. */
    ...(data.capBands === undefined ? SEED_CAP_BANDS : data.capBands)
      .map((v) => tx.objectStore('capBands').put(v)),
    ...data.holdings.map((v) => tx.objectStore('holdings').put(v)),
    ...data.theses.map((v) => tx.objectStore('theses').put(v)),
    ...data.trades.map((v) => tx.objectStore('trades').put(v)),
    ...data.cashflows.map((v) => tx.objectStore('cashflows').put(v)),
    ...data.snapshots.map((v) => tx.objectStore('snapshots').put(v)),
    ...data.perps.map((v) => tx.objectStore('perps').put(v)),
    ...data.cash.map((v) => tx.objectStore('cash').put(v)),
    ...data.themes.map((v) => tx.objectStore('themes').put(v)),
    ...data.caps.map((v) => tx.objectStore('caps').put(v)),
    // Absent from any export written before the radar existed.
    ...(data.watchlist ?? []).map((v) => tx.objectStore('watchlist').put(v)),
    ...(data.importBatches ?? []).map((v) => tx.objectStore('importBatches').put(v)),
    ...(data.alertLog ?? []).map((v) => tx.objectStore('alertLog').put(v)),
  ]);
  await tx.done;
}

export async function put<K extends 'holdings' | 'theses' | 'trades' | 'cashflows' | 'snapshots' | 'perps' | 'cash' | 'themes' | 'caps' | 'accounts' | 'dividends' | 'watchlist' | 'importBatches' | 'alertLog' | 'conversions' | 'transfers' | 'capBands'>(
  store: K, value: BookDB[K]['value'],
): Promise<void> {
  await (await db()).put(store, value as never);
}

export async function remove(
  store: 'holdings' | 'theses' | 'trades' | 'cashflows' | 'snapshots' | 'perps' | 'themes' | 'caps' | 'accounts' | 'dividends' | 'watchlist' | 'importBatches' | 'alertLog' | 'conversions' | 'capBands',
  key: string,
): Promise<void> {
  await (await db()).delete(store, key);
}

/** Wipes the device's book and loads the starting positions again. The seeded
    flag is cleared first so the normal seeding path does the work — there is
    only one definition of what the starting book is. */
export async function resetBook(): Promise<void> {
  const d = await db();
  await Promise.all([
    d.clear('quotes'), d.clear('earnings'), d.clear('perpMarks'),
    d.clear('financials'), d.clear('divHistory'),
  ]);
  await saveSettings({ seeded: false });
  await seedIfEmpty(true);
}

/** First run only. Loads §10 so the book is usable before anything is typed. */
export async function seedIfEmpty(force = false): Promise<boolean> {
  const settings = await getSettings();
  if (settings.seeded && !force) return false;
  const d = await db();
  if (!force && (await d.count('holdings')) > 0) {
    await saveSettings({ seeded: true });
    return false;
  }
  await replaceBook({
    accounts: SEED_ACCOUNTS,
    dividends: [],
    holdings: SEED_HOLDINGS,
    theses: [],
    trades: [],
    cashflows: [],
    snapshots: SEED_SNAPSHOTS,
    perps: SEED_PERPS,
    cash: SEED_CASH,
    themes: SEED_THEMES,
    caps: SEED_CAPS,
    // §10 follows nothing it does not own; the radar starts empty.
    watchlist: [],
    importBatches: [],
    alertLog: [],
    conversions: [], transfers: [], capBands: SEED_CAP_BANDS,
  });
  if (!(await getFx())) {
    await saveFx({ ...SEED_FX, fetchedAt: new Date(0).toISOString() });
  }
  // Observed marks, so the book opens on real prices where they are known. Each
  // carries its own timestamp and is overwritten by the first live quote.
  if (force || (await d.count('quotes')) === 0) await saveQuotes(SEED_QUOTES);
  await saveSettings({ seeded: true });
  return true;
}
